import { Box, Button, Typography } from '@mui/material'
import { forwardRef } from 'react'
import { TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { useBrowserLink } from '@/components/lib/BrowserLink'
import {
  handleError,
  tLabel,
  useMessage,
  useRouter,
  useTranslation,
} from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'
import { getHelpUrl } from '@/lib/helpUrl'

export type MailAuthArtistFormProps = {
  loading: boolean
  onSubmit: (inputs: MailAuthFormValues) => Promise<void>
  onResendAuthMail: () => Promise<void>
}

export type MailAuthFormValues = {
  code: string
}

const schema: UseFormProps['schema'] = o => ({
  code: o.string().length(6).label(tLabel('mailAuthArtist.code')),
})

export const MailAuthArtistForm: Organism<MailAuthArtistFormProps> = ({
  loading,
  onSubmit,
  onResendAuthMail,
}) => {
  const { t } = useTranslation('mailAuthArtist')
  const { showMessage, showError } = useMessage()
  const { router, routerPages } = useRouter()

  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<MailAuthFormValues>({
    schema,
  })

  const onVerify = handleSubmit(values =>
    onSubmit(values)
      .then(() => {
        router.push(routerPages.ProfileInputArtist)
      })
      .catch(e => handleError<MailAuthFormValues>(e, setError, showError))
  )

  const onReSend = () =>
    onResendAuthMail()
      .then(() => showMessage(t('resended')))
      .catch(e => showError(e))

  return (
    <Form onSubmit={onVerify}>
      <TextInputCell type="text" error={errors?.code} {...register('code')} />
      <ResendButtonCell onResendAuthMail={onReSend} loading={loading} />
      <ButtonCell loading={isSubmitting} />
      <NoEmailReceivedCell />
    </Form>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('mailAuthArtist')

    return (
      <CellFrame>
        <TextInputField
          {...props}
          ref={ref}
          t={t}
          textFieldProps={{ sx: { mb: 0 } }}
          titleProps={{ sx: { display: 'none' } }}
        />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  loading: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading }) => {
  const { t } = useTranslation('mailAuth')

  return (
    <CellFrame sx={{ py: 2, textAlign: 'center' }}>
      <Button type="submit" size="large" sx={{ width: 244 }} loading={loading}>
        {t('submit')}
      </Button>
    </CellFrame>
  )
}

const ResendButtonCell: Cell<{
  onResendAuthMail: () => void
  loading: boolean
}> = ({ onResendAuthMail, loading }) => {
  const { t } = useTranslation('mailAuth')

  return (
    <CellFrame>
      <Box sx={{ textAlign: 'right' }}>
        <Button
          size="small"
          variant="text"
          onClick={onResendAuthMail}
          loading={loading}
        >
          <Typography variant="caption" sx={{ color: 'primary.main' }}>
            {t('resend')}
          </Typography>
        </Button>
      </Box>
    </CellFrame>
  )
}

/**
 * 認証メールが届かない場合のリンク表示用セル
 */
const NoEmailReceivedCell: Cell = () => {
  const { t } = useTranslation('mailAuth')
  const { onClick } = useBrowserLink(getHelpUrl('noEmailReceived'))

  return (
    <CellFrame sx={{ py: 3, textAlign: 'center' }}>
      <Typography
        component="span"
        variant="subtitle1"
        color="primary.main"
        sx={{ fontSize: 14, cursor: 'pointer' }}
        onClick={onClick}
      >
        {t('noEmailReceived')}
      </Typography>
    </CellFrame>
  )
}
